import React, { useContext, useEffect, useState } from 'react';

import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Button, Typography } from '@mui/material';
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import { db } from '../../../api/firebase/config';
import { companiesRef } from '../../../api/firebase/firestore/company';
import { mediasRef } from '../../../api/firebase/firestore/media';
import { blackButton } from '../../../constants/Colors';
import {
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../../constants/Location';
import { screenType, status } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { throwError } from '../../../modules/error';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import MediaCard from '../../uiParts/MediaCard';
import NothingGivenList from '../../uiParts/NothingGivenList';
import Sidebar from '../../uiParts/Sidebar';

import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

const styles = {
  blackButton,
  card: {
    '@media screen and (max-width:470px)': {
      mt: '12px'
    },
    mt: '24px'
  },
  comparisonButton: {
    ':disabled': {
      background: '#E0E0E0'
    },
    columnGap: '8px',
    display: 'flex',
    mt: '24px',
    padding: '6px 16px'
  },
  comparisonButtonIcon: {
    height: '20px',
    width: '20px'
  },
  comparisonButtonText: {
    fontSize: '14px'
  },
  list: {
    '@media screen and (max-width:470px)': {
      margin: '0 16px'
    },
    margin: '0 24px'
  },
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
};

const ClientMypageFavoriteMediaList: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    document.title = t('title.client.favoriteMedia');
  });

  interface Media {
    mediaId: string;
    companyId: number;
    created: Date;
    updated: Date;
    status: number;
    assigneeId: number;
    name: string;
    category: [];
    overview: string;
    funnel: number[];
    mediaType: number;
    target: number[];
    seasonalMenuMaterialText: string;
    value: string;
    industries: Array<{
      industriesNum: number;
      industriesOther: string;
      percentage: number;
    }>;
    continuousFactors: string[];
    image: string;
    demographicsGeneration:
      | {
          f10: number;
          f20: number;
          f30: number;
          f40: number;
          f50: number;
          f60: number;
          f70: number;
          m10: number;
          m20: number;
          m30: number;
          m40: number;
          m50: number;
          m60: number;
          m70: number;
        }
      | undefined;
    demographicsGender:
      | {
          male: number;
          female: number;
        }
      | undefined;
    favoriteCount: number;
  }

  const [hasMore, setHasMore] = useState(true);
  const [medias, setMedias] = useState<Media[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);

  const appendQuestions = async (
    snapshot: Array<QueryDocumentSnapshot<DocumentData>>
  ): Promise<void> => {
    const companies = query(
      companiesRef,
      where('status', '==', status.approved)
    );

    const companiesSnaps = await getDocs(companies);

    let activeCompanyIds: string[] = [];
    companiesSnaps.forEach((companySnap) => {
      if (companySnap.data().status === status.approved) {
        activeCompanyIds.push(companySnap.data().companyId as string);
      }
    });

    activeCompanyIds = activeCompanyIds.filter((item) => item !== undefined);

    let gotMedias: Media[] = snapshot.map((document) => {
      const media = document.data() as Media;
      return media;
    });

    gotMedias = gotMedias.filter((media) =>
      activeCompanyIds.includes(`${media.companyId}`)
    );

    setMedias(medias.concat(gotMedias));
  };

  const fetchFavoriteMedias = async (
    uid: string,
    lastMedia: Media | null | undefined
  ): Promise<QueryDocumentSnapshot[]> => {
    let favoriteMediasQuery;

    if (lastMedia === null || lastMedia === undefined) {
      favoriteMediasQuery = query(
        collectionGroup(db, 'favoriteMedia'),
        where('uid', '==', uid),
        orderBy('created', 'desc'),
        limit(50)
      );
    } else {
      favoriteMediasQuery = query(
        collectionGroup(db, 'favoriteMedia'),
        where('uid', '==', uid),
        orderBy('created', 'desc'),
        startAfter(lastMedia?.created),
        limit(50)
      );
    }

    const favoriteMediasSnap = await getDocs(favoriteMediasQuery);

    const mediaPromises = favoriteMediasSnap.docs.map(
      async (favoriteMediaSnap) => {
        const favoriteMediaData = favoriteMediaSnap.data();
        const mediaId = favoriteMediaData?.mediaId as string;
        if (!mediaId) {
          return null;
        }
        const mediaRef = doc(mediasRef, mediaId);
        const mediaSnap = await getDoc(mediaRef);
        const mediaData = mediaSnap.data();
        if (!mediaSnap.exists() || mediaData?.status !== status.approved) {
          return null;
        }

        return mediaSnap;
      }
    );

    const mediaSnaps: Array<QueryDocumentSnapshot | null> = await Promise.all(
      mediaPromises
    );
    const mediaQueryDocumentSnapshot = mediaSnaps.filter(
      (mediaSnap) => mediaSnap !== null
    ) as QueryDocumentSnapshot[];

    return mediaQueryDocumentSnapshot;
  };

  const loadMore = async (): Promise<void> => {
    if (currentUser) {
      let lastMedia;
      if (medias.length > 0) {
        lastMedia = medias[medias.length - 1];
      }

      let datas;
      if (medias.length) {
        lastMedia = medias[medias.length - 1];
        datas = await fetchFavoriteMedias(currentUser.uid, lastMedia);
      } else {
        datas = await fetchFavoriteMedias(currentUser.uid, null);
      }

      if (datas === null) {
        setHasMore(false);
        return;
      }
      await appendQuestions(datas);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await loadMore();
      } catch (e) {
        throwError(e);
      } finally {
        setShowList(true);
        setLoading(false);
      }
    })();
  }, [currentUser, medias.length]);

  // メディア比較
  const [compareMedias, setCompareMedias] = useState<string[]>([]);
  const [compareButtonDisabled, setCompareButtonDisabled] =
    useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');

  const handleCheckboxChange = (id: string): void => {
    setCompareMedias((prev) => {
      const index = prev.indexOf(id);
      if (index === -1) {
        return [...prev, id];
      }
      return prev.filter((prevId) => prevId !== id);
    });
  };

  useEffect(() => {
    setCompareButtonDisabled(
      compareMedias.length < 1 || compareMedias.length > 10
    );
    if (compareMedias.length > 10) {
      setErrorText(t('media.compareError'));
      setOpen(true);
    }
  }, [compareMedias]);

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.mypageWrapper}>
        <Sidebar screenType={screenType.client} />
        <Typography
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            position: 'static',
            width: '100%'
          }}
        >
          <Typography sx={styles.mypageHeader} component="div">
            <Typography sx={styles.mypageHeaderTitle}>
              {t('mypage.favoriteMedia')}
            </Typography>
          </Typography>
          <Typography component="div" sx={{ ml: '24px' }}>
            <Button
              sx={[styles.comparisonButton, styles.blackButton]}
              disabled={compareButtonDisabled}
              onClick={() => {
                window.open(
                  `/main/client/mypage/media/compare?mediaIds=${compareMedias.join(
                    ','
                  )}`
                );
              }}
            >
              <Typography component="div" sx={styles.comparisonButtonText}>
                {t('media.mediaComparison')}
              </Typography>
              <OpenInNewOutlinedIcon sx={styles.comparisonButtonIcon} />
            </Button>
          </Typography>
          {showList && (
            <Typography component="div" sx={{ mb: '40px' }}>
              {medias.length !== 0 ? (
                <Typography sx={styles.list} component="div">
                  <InfiniteScroll
                    loadMore={() => {
                      void (async () => {
                        await loadMore();
                      })();
                    }}
                    hasMore={hasMore}
                    loader={<div className="loader" key={0}></div>}
                  >
                    {medias.map((value, i) => (
                      <Typography key={i} sx={styles.card} component="div">
                        <MediaCard
                          screen={screenType.client}
                          category={value?.category}
                          image={value?.image}
                          title={value?.name}
                          summaryText={value?.overview}
                          funnel={value?.funnel}
                          mediaType={value?.mediaType}
                          target={value?.target}
                          seasonalMenuMaterialText={
                            value?.seasonalMenuMaterialText
                          }
                          industries={value?.industries}
                          demographicsGeneration={value?.demographicsGeneration}
                          demographicsGender={value?.demographicsGender}
                          hasCheck
                          mediaName={value?.name}
                          mediaId={value?.mediaId}
                          checked={compareMedias.includes(value?.mediaId)}
                          onCheckboxChange={handleCheckboxChange}
                        />
                      </Typography>
                    ))}
                  </InfiniteScroll>
                </Typography>
              ) : (
                <NothingGivenList
                  title={t('media.nofavoriteMedia')}
                  detail={t('media.nofavoriteMediaDesc')}
                  type="mediaFavorite"
                  screen={screenType.client}
                />
              )}
            </Typography>
          )}
        </Typography>
      </Typography>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default ClientMypageFavoriteMediaList;
